/**
 * References between documents (RFC 0275).
 *
 * A document id and a REFERENCE to a document are different things, and this
 * codebase has been writing both as the same bare base58 string. That is what
 * automerge-repo calls a `DocumentId`; the reference form is an `AutomergeUrl`,
 * `automerge:<id>`, and the library has shipped `stringifyAutomergeUrl` /
 * `parseAutomergeUrl` / `interpretAsDocumentId` the whole time.
 *
 * WHY IT MATTERS, and it is not tidiness. A bare id is indistinguishable from
 * prose: several appear in running text in the plans we edited this week. So
 * "find the documents this document refers to" is undecidable by inspection,
 * which is exactly the question the sandbox gate has to answer before handing an
 * app a real Automerge handle. With a prefix the question is decidable, and the
 * gate can stop guessing.
 *
 * It also makes marker rewriting possible: `automerge:<id>` can be found and
 * replaced in content so an ungranted id never reaches an app. A bare id cannot,
 * for the same false-positive reason.
 *
 * MIGRATION POSTURE: readers take BOTH forms, writers emit URLs. Every stored
 * reference is bare today, so nothing has to be un-migrated, and a reader that
 * normalises cannot tell which era a value came from.
 *
 * Catalog KEYS stay bare. A key is not a reference, nothing scans it, and
 * rewriting the `documents` / `apps` / `sessionMeta` maps would be churn with no
 * security value.
 */

import { ImmutableString } from '@automerge/automerge/slim'
import {
  interpretAsDocumentId,
  isValidAutomergeUrl,
  isValidDocumentId,
  stringifyAutomergeUrl,
} from '@automerge/automerge-repo/slim'

/** Project catalogs moved to one-op strings (RFC 0357 S11). Keep bare and
 * URL references readable across both encodings without coercing arbitrary
 * objects into purported document references. */
function referenceText(value: unknown): string | null {
  if (typeof value === 'string') return value
  return value instanceof ImmutableString ? String(value) : null
}

/** `automerge:<id>`. What a stored reference should look like from now on. */
export type DocumentRefUrl = string

/**
 * The reference form of a document id. Idempotent: pass a URL, get it back.
 *
 * Returns null rather than throwing for anything that is not a document id,
 * because callers are usually mapping over values they did not author, and a
 * throw would turn one bad field into a blank page.
 */
export function toDocumentRef(value: unknown): DocumentRefUrl | null {
  const text = referenceText(value)
  if (!text) return null
  if (isValidAutomergeUrl(text)) return text
  if (!isValidDocumentId(text)) return null
  return stringifyAutomergeUrl({ documentId: text })
}

/**
 * The bare id, from either form. THE READ PATH: everything that resolves a
 * reference goes through here, so a stored value written before or after the
 * migration resolves identically.
 */
export function toDocumentId(value: unknown): string | null {
  const text = referenceText(value)
  if (!text) return null
  try {
    return interpretAsDocumentId(text as never) as unknown as string
  } catch {
    return null
  }
}

/** Is this a reference, unambiguously? Bare ids deliberately do NOT count: the
 *  whole point is that a bare string cannot be trusted to be one. */
export function isDocumentRef(value: unknown): value is DocumentRefUrl {
  return typeof value === 'string' && isValidAutomergeUrl(value)
}

/**
 * Every document this text refers to.
 *
 * Safe to run over PROSE, which is the property the bare form could never have.
 * Used by the sandbox gate to widen an app's allowlist to the documents a
 * granted document actually points at, without a schema saying where to look.
 */
export function findDocumentRefs(text: unknown): string[] {
  if (typeof text !== 'string' || !text.includes('automerge:')) return []
  const out: string[] = []
  for (const match of text.matchAll(/automerge:[1-9A-HJ-NP-Za-km-z]+/g)) {
    const id = toDocumentId(match[0])
    if (id) out.push(id)
  }
  return [...new Set(out)]
}
